import { useUserProfile } from "@/context/UserProfileContext";
import { useOnboarding } from "@/hooks/use-onboarding";
import { api } from "@/utils/api";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import {
  IonButton,
  IonContent,
  IonFooter,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSpinner,
  IonToolbar,
} from "@ionic/react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { motion } from "framer-motion";
import { checkmarkCircleOutline, closeCircleOutline } from "ionicons/icons";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { z } from "zod";
import GolfLoadingTips from "../GolfLoadingTips";

interface UsernameSelectionProps {
  nextStep: string;
  setLoadingState?: (loading: boolean, hideHeader?: boolean) => void;
}

const UsernameSelection: React.FC<UsernameSelectionProps> = ({ nextStep, setLoadingState }) => {
  const history = useHistory();
  const { updateOnboardingData, onboardingData } = useOnboarding();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isUsernameVerified, setIsUsernameVerified] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [usernameValid, setUsernameValid] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { refreshProfile } = useUserProfile();

  useEffect(() => {
    if (onboardingData.username) {
      setFormData(onboardingData);
      setUsernameValid(validateUsername(onboardingData.username));
      debouncedCheckUsername(onboardingData.username);
    }
  }, [onboardingData]);

  const formSchema = z.object({
    username: z
      .string()
      .min(2, t("username_too_short"))
      .max(32, t("username_too_long"))
      .regex(/^[a-z0-9-.]+$/i, t("username_invalid_characters")),
  });

  const validateUsername = (username: string) => {
    const regex = /^[a-z0-9-.]+$/i;
    return regex.test(username) && username.length >= 2 && username.length <= 32;
  };

  const checkUsername = async (username: string) => {
    if (username.length < 2) return;

    setIsChecking(true);
    setIsUsernameVerified(false);

    try {
      const { data } = await api.get(`/username/${encodeURIComponent(username)}`);
      if (data === false) {
        setIsUsernameVerified(true);
        setErrors({ ...errors, username: "" });
      } else {
        setErrors({ ...errors, username: t("username_already_used") });
      }
    } catch (error) {
      setErrors({ ...errors, username: t("username_forbidden") });
    } finally {
      setIsChecking(false);
    }
  };

  // Debounce function
  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debouncedCheckUsername = useCallback(debounce(checkUsername, 500), []);

  const handleInputChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });

    if (name === "username") {
      setUsernameValid(validateUsername(value));
      if (value) {
        setIsChecking(true);
        debouncedCheckUsername(value);
      } else {
        setIsChecking(false);
        setIsUsernameVerified(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setLoadingState?.(true, true);

      formSchema.parse(formData);
      if (!isUsernameVerified) {
        setErrors({ ...errors, username: t("username_not_verified") });
        setIsSubmitting(false);
        setLoadingState?.(false);
        return;
      }

      // First update onboarding data
      await updateOnboardingData({ username: formData.username });

      // Then create the profile
      const response = await api.post(`/profile/${formData.username}`, {
        firstName: onboardingData.firstName,
        lastName: onboardingData.lastName,
        username: formData.username,
        countryCode: onboardingData.countryCode,
        imageUrl: onboardingData.imageUrl,
        gender: onboardingData.gender,
        handicap: onboardingData.handicap,
        membership: onboardingData.membership,
        motivation: onboardingData.motivation,
      });

      if (response.status === 200 || response.status === 201) {
        const currentUser = await FirebaseAuthentication.getCurrentUser();
        if (currentUser && currentUser.user) {
          await FirebaseAuthentication.updateProfile({
            displayName: formData.username,
            photoUrl: onboardingData.imageUrl,
          });

          // Short delay to show the creating profile screen
          await new Promise(resolve => setTimeout(resolve, 3000));

          // Navigate to the next step
          refreshProfile();
          setLoadingState?.(false);
          history.push(nextStep);
        } else {
          setIsSubmitting(false);
          setLoadingState?.(false);
          console.error('Failed to create profile:', response.data);
          setErrors({ ...errors, submit: t('profile_creation_failed') });
        }
      } else {
        setIsSubmitting(false);
        setLoadingState?.(false);
        console.error('Failed to create profile:', response.data);
        setErrors({ ...errors, submit: t('profile_creation_failed') });
      }
    } catch (error) {
      console.error("Error creating profile:", error);
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        setErrors({
          ...errors,
          form: "Error creating profile",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <IonPage>
      {isSubmitting ? (
        <div className="flex flex-col items-center justify-center h-full bg-card">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col items-center justify-center p-6 rounded-lg mx-auto"
          >
            <DotLottieReact className="size-64 p-8" src="/assets/lottie/cart.json" autoplay loop />
            <h2 className="text-xl font-semibold text-center mb-3">{t("creating_profile", "Création du profil")}</h2>
            <GolfLoadingTips />
          </motion.div>
        </div>
      ) : (
        <>
          <IonContent>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col items-center justify-center ion-padding bg-card h-full"
            >
              <IonList className="space-y-6 text-start" lines="none">
                <h1 className="text-4xl font-bold">{t("username")}</h1>
                <sub className="text-base text-muted-foreground">{t("pick_username_description")}</sub>

                <IonItem className="custom-item">
                  <IonInput
                    autofocus
                    value={formData.username}
                    className="text-2xl"
                    fill="outline"
                    onIonInput={(e) => {
                      const username = e.detail.value!;
                      handleInputChange("username", username);
                    }}
                    placeholder={t("username") + "..."}
                  />

                  {(usernameValid !== null && !isChecking && errors.username != null) && (
                    <IonIcon
                      color={
                        usernameValid && errors?.username === ""
                          ? "success"
                          : "danger"
                      }
                      icon={
                        usernameValid && errors.username === ""
                          ? checkmarkCircleOutline
                          : closeCircleOutline
                      }
                    />
                  )}
                  {isChecking && <IonSpinner className="size-5" name="crescent" />}
                </IonItem>
                {errors.username && <IonLabel color="danger">{errors.username}</IonLabel>}
                {isChecking && <IonLabel>{t("checking_username_availability")}</IonLabel>}
                {(isUsernameVerified && !isChecking) && <IonLabel color="success">{t("username_available")}</IonLabel>}
                {errors.submit && <IonLabel color="danger">{errors.submit}</IonLabel>}
                <IonButton
                  type="button"
                  shape="round"
                  expand="full"
                  className="mt-8 hidden md:block"
                  onClick={handleSubmit}
                  disabled={!isUsernameVerified || isChecking || isSubmitting}
                >
                  <div className="flex items-center justify-center gap-2">
                    {t("create_profile")}
                    {isSubmitting && <IonSpinner name="dots" />}
                  </div>
                </IonButton>
              </IonList>
            </motion.div>
          </IonContent>
          <IonFooter className="bg-card ion-no-border md:hidden">
            <IonToolbar>
              <IonButton
                type="button"
                shape="round"
                expand="block"
                className="mx-2"
                onClick={handleSubmit}
                disabled={!isUsernameVerified || isChecking || isSubmitting}
              >
                <div className="flex items-center justify-center gap-2">
                  {t("create_profile")}
                  {isSubmitting && <IonSpinner name="dots" />}
                </div>
              </IonButton>
            </IonToolbar>
          </IonFooter>
        </>
      )
      }
    </IonPage >
  );
};
export default UsernameSelection;
